// src/auth/MsalProvider.tsx
import React, { useEffect, useState } from 'react';
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { msalConfig } from './authConfig';

export const msalInstance = new PublicClientApplication(msalConfig);

interface MsalProviderProps {
  // eslint-disable-next-line no-unused-vars
  onMsalReady?: (instance: PublicClientApplication) => void; // Make this optional
  children: React.ReactNode;
}

const MsalProvider: React.FC<MsalProviderProps> = ({ onMsalReady, children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    msalInstance.initialize().then(() => {
      // Handle redirect promise to catch returning authentication responses
      msalInstance
        .handleRedirectPromise()
        .then((response: AuthenticationResult | null) => {
          if (response) {
            msalInstance.setActiveAccount(response.account);
          }
        })
        .catch(() => {
          // Silently handle redirect errors
        });

      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      }

      msalInstance.addEventCallback((event: EventMessage) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          const payload = event.payload as AuthenticationResult;
          msalInstance.setActiveAccount(payload.account);
        }

        if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
          const payload = event.payload as AuthenticationResult;
          msalInstance.setActiveAccount(payload.account);
        }

        if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
          const error = event.error;
          if (error instanceof InteractionRequiredAuthError) {
            // Clear the active account when interaction is required
            // This will force the user to re-authenticate
          }
        }
      });

      setIsInitialized(true);

      if (onMsalReady) {
        onMsalReady(msalInstance); // Call onMsalReady only if provided
      }
    });
  }, [onMsalReady]);

  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  return <>{children}</>;
};

export default MsalProvider;
